import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui/button';
import { RefreshCw, X } from 'lucide-react';
import { useState, useEffect } from 'react';

// Use globalThis for true singleton across module duplicates (HMR, chunk splits, etc.)
const PWA_SINGLETON = ((globalThis as any).__atlasdraftPwaUpdate ??= {
  bannerVisible: false,
  intervalId: null as ReturnType<typeof setInterval> | null,
  initialized: false,
});

export function PWAUpdateNotification() {
  const [dismissed, setDismissed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      // Only initialize the interval once per app session globally
      if (PWA_SINGLETON.initialized || !registration) return;
      PWA_SINGLETON.initialized = true;
      
      // Clear any existing interval before setting a new one
      if (PWA_SINGLETON.intervalId) {
        clearInterval(PWA_SINGLETON.intervalId);
      }
      
      // Check for updates every hour
      PWA_SINGLETON.intervalId = setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);
    },
  });

  // Handle global banner singleton with DOM-level dedupe
  useEffect(() => {
    if (needRefresh && !dismissed) {
      // Check DOM for existing banner - ultimate deduplication
      const existingBanners = document.querySelectorAll('#pwa-update-banner');
      if (existingBanners.length === 0 && !PWA_SINGLETON.bannerVisible) {
        PWA_SINGLETON.bannerVisible = true;
        setShouldRender(true);
      }
    } else {
      if (shouldRender) {
        PWA_SINGLETON.bannerVisible = false;
        setShouldRender(false);
      }
    }
    
    return () => {
      if (shouldRender) {
        PWA_SINGLETON.bannerVisible = false;
      }
    };
  }, [needRefresh, dismissed, shouldRender]);

  const handleUpdate = () => {
    setIsUpdating(true);
    updateServiceWorker(true).then(() => {
      // Small delay to ensure SW is properly activated before reload
      setTimeout(() => window.location.reload(), 500);
    }).catch(() => {
      setIsUpdating(false);
    });
  };

  if (!shouldRender) return null;

  return (
    <div 
      id="pwa-update-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="bg-card border border-border rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-foreground">Update Available</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              A new version of AtlasDraft is ready. Refresh to get the latest features.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleUpdate}
                disabled={isUpdating}
                className="h-8 text-xs"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${isUpdating ? 'animate-spin' : ''}`} />
                {isUpdating ? 'Updating...' : 'Update Now'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDismissed(true)}
                className="h-8 text-xs"
              >
                Later
              </Button>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
